type Options<T> = {
  /** Loads one page. Resolve null/undefined for a failed load; it is simply not cached. */
  fetch: (page: number) => Promise<T | null | undefined>;
  /** Maximum number of completed pages retained. */
  maxPages?: number;
  /** Freshness budget measured from the original fetch, including pages handed back with put(). */
  maxAgeMs?: number;
};

/**
 * Speculatively loads neighbouring pages of a server-paged list so the next page change can be
 * served from memory. Keys are page numbers only: every other input (sort, filters, page size,
 * data mutations) must call clear(), which also discards any prefetch still in flight.
 */
export const usePagePrefetch = <T>(options: Options<T>) => {
  const cached = new Map<number, { value: T; fetchedAt: number }>();
  const inFlight = new Set<number>();
  const maxPages = Math.max(1, options.maxPages ?? 3);
  const maxAgeMs = options.maxAgeMs ?? 30_000;
  let generation = 0;

  const prune = (): void => {
    const now = Date.now();
    cached.forEach((entry, page) => {
      if (now - entry.fetchedAt >= maxAgeMs) {
        cached.delete(page);
      }
    });
  };

  /** The original fetch time must survive a page being displayed and handed back. */
  const put = (page: number, value: T, fetchedAt = Date.now()): void => {
    prune();
    cached.delete(page);
    if (Date.now() - fetchedAt >= maxAgeMs) {
      return;
    }
    cached.set(page, { value, fetchedAt });
    while (cached.size > maxPages) {
      cached.delete(cached.keys().next().value!);
    }
  };

  const prime = (pages: number[]): void => {
    prune();
    pages.forEach(page => {
      if (cached.has(page) || inFlight.has(page)) {
        return;
      }

      const startedIn = generation;
      const fetchedAt = Date.now();
      inFlight.add(page);

      options.fetch(page)
        .then(result => {
          if (startedIn === generation && result !== null && result !== undefined) {
            put(page, result, fetchedAt);
          }
        })
        .catch(() => undefined)
        .finally(() => {
          if (startedIn === generation) {
            inFlight.delete(page);
          }
        });
    });
  };

  /** Returns the cached page, if any, and forgets it: a page is served once, then refetched if needed. */
  const takeEntry = (page: number): { value: T; fetchedAt: number } | undefined => {
    prune();
    const result = cached.get(page);
    cached.delete(page);

    return result;
  };

  const take = (page: number): T | undefined => takeEntry(page)?.value;

  const has = (page: number): boolean => {
    prune();
    return cached.has(page);
  };

  const clear = (): void => {
    generation += 1;
    cached.clear();
    inFlight.clear();
  };

  return {
    prime,
    take,
    takeEntry,
    has,
    put,
    clear,
  };
};
